import type { FC } from 'react';
import { Fragment, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { Divider } from '@mui/material';
import {
  AdsetsLabel,
  ColumnStack,
  EmptyStateText,
  NameCell,
  PreviewCard,
  SectionLabel,
  TemplateHeaderRow,
  TemplateRow,
  ValueColumn,
} from './LaunchPreview.styles';
import type { LaunchPreviewValues } from './types';
import { adSetBudgetDisplay, campaignBudgetDisplay, groupAdSetsByCampaignId } from './utils';

const TemplatesPreview: FC = () => {
  const [selectedCampaigns, selectedAdSets, campaignBudgets, adSetBudgets] = useWatch<
    LaunchPreviewValues,
    ['selectedCampaigns', 'selectedAdSets', 'selectedCampaignsBudgets', 'selectedAdSetsBudgets']
  >({
    name: [
      'selectedCampaigns',
      'selectedAdSets',
      'selectedCampaignsBudgets',
      'selectedAdSetsBudgets',
    ],
  });

  const adSetsByCampaignId = useMemo(
    () => groupAdSetsByCampaignId(selectedAdSets),
    [selectedAdSets],
  );

  if (!selectedCampaigns?.length) {
    return <EmptyStateText variant="body2">No campaigns selected</EmptyStateText>;
  }

  return (
    <ColumnStack>
      {selectedCampaigns.map((campaign) => (
        <PreviewCard key={campaign.id}>
          <SectionLabel variant="caption" color="text.secondary">
            Campaign
          </SectionLabel>
          <TemplateHeaderRow>
            <NameCell variant="body2" fontWeight={700}>
              {campaign.name}
            </NameCell>
            <ValueColumn variant="body2">
              {campaignBudgetDisplay(campaign, campaignBudgets)}
            </ValueColumn>
          </TemplateHeaderRow>
          <AdsetsLabel variant="caption" color="text.secondary">
            Adsets
          </AdsetsLabel>
          {(adSetsByCampaignId[campaign.id] ?? []).map((adSet, index) => (
            <Fragment key={adSet.id}>
              {index > 0 && <Divider />}
              <TemplateRow>
                <NameCell variant="body2">{adSet.name}</NameCell>
                <ValueColumn variant="body2">
                  {adSetBudgetDisplay(adSet, adSetBudgets)}
                </ValueColumn>
              </TemplateRow>
            </Fragment>
          ))}
        </PreviewCard>
      ))}
    </ColumnStack>
  );
};

export default TemplatesPreview;
